import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Play, CheckCircle, Clock, AlertCircle, Award, Target, FileText, Calendar, Plus, ChevronDown, ChevronRight, Edit, Download } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import myRecommendationService from '../../../../../services/audit_services/myRecommendationService';
import followupAttachmentService from '../../../../../services/audit_services/followupAttachmentService';

export default function MyRecommendationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens, hasRole, hasAnyRole } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [recommendation, setRecommendation] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [expandedFollowUps, setExpandedFollowUps] = useState(new Set());
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [editFollowUpForm, setEditFollowUpForm] = useState({
    progression_percentage: 0,
    recommendation_status_selected: 'in_progress',
    notes: '',
    attachments: []
  });

  // Follow-up form state
  const [showCreateFollowUp, setShowCreateFollowUp] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({
    progression_percentage: 0,
    recommendation_status_selected: 'in_progress',
    notes: '',
    attachments: []
  });

  // const canManage = hasRole('AuditUser') || hasRole('AuditManager');
  const roleNames = ["Admin","AuditManager", "AuditUser", "RiskManager", "RiskIdentifier", "CartoViewer"]
  const canManage = hasAnyRole(roleNames);

  useEffect(() => {
    fetchRecommendationDetails();
    fetchFollowUps();
  }, [id]);

  const fetchRecommendationDetails = async () => {
    setLoading(true);
    
    try {
      const result = await myRecommendationService.getMyRecommendationDetail(authTokens.access, id);
      
      if (result.success) {
        setRecommendation(result.data);
      } else {
        showError(result.error || 'Error loading recommendation details');
        navigate('/dashboard/audit/my-recommendations');
      }
    } catch (error) {
      console.error('Error fetching recommendation details:', error);
      showError('Error loading recommendation details');
      navigate('/dashboard/audit/my-recommendations');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUps = async () => {
    setFollowUpsLoading(true);
    
    try {
      const result = await myRecommendationService.getRecommendationFollowUps(
        authTokens.access, 
        id,
        { ordering: 'created_at_desc' },
        { page_size: 5 }
      );
      
      if (result.success) {
        setFollowUps(result.data.results || []);
      } else {
        console.error('Error loading follow-ups:', result.error);
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const handleStartRecommendation = async () => {
    setActionLoading(prev => ({ ...prev, start: true }));
    
    try {
      const result = await myRecommendationService.startMyRecommendation(authTokens.access, id);
      
      if (result.success) {
        showSuccess('Recommendation started successfully');
        fetchRecommendationDetails(); // Refresh the data
      } else {
        showError(result.error || 'Error starting recommendation');
      }
    } catch (error) {
      console.error('Error starting recommendation:', error);
      showError('Error starting recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, start: false }));
    }
  };

  const handleCompleteRecommendation = async () => {
    setActionLoading(prev => ({ ...prev, complete: true }));
    
    try {
      const result = await myRecommendationService.completeMyRecommendation(authTokens.access, id);
      
      if (result.success) {
        showSuccess('Recommendation completed successfully');
        fetchRecommendationDetails(); // Refresh the data
      } else {
        showError(result.error || 'Error completing recommendation');
      }
    } catch (error) {
      console.error('Error completing recommendation:', error);
      showError('Error completing recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, complete: false }));
    }
  };

  const handleAchieveRecommendation = async () => {
    setActionLoading(prev => ({ ...prev, achieve: true }));
    
    try {
      const result = await myRecommendationService.achieveMyRecommendation(authTokens.access, id);
      
      if (result.success) {
        showSuccess('Recommendation achieved successfully');
        fetchRecommendationDetails(); // Refresh the data
      } else {
        showError(result.error || 'Error achieving recommendation');
      }
    } catch (error) {
      console.error('Error achieving recommendation:', error);
      showError('Error achieving recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, achieve: false }));
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    
    if (!followUpForm.notes.trim()) {
      showError('Notes are required');
      return;
    }

    setFollowUpLoading(true);
    
    try {
      // First create the follow-up
      const followUpData = myRecommendationService.formatFollowUpData(followUpForm);
      const result = await myRecommendationService.createFollowUp(
        authTokens.access, 
        id, 
        followUpData
      );
      
      if (result.success) {
        const followUpId = result.data.id;
        
        // Upload attachments if any
        if (followUpForm.attachments && followUpForm.attachments.length > 0) {
          for (const file of followUpForm.attachments) {
            try {
              await myRecommendationService.uploadFollowUpAttachment(
                authTokens.access,
                id,
                followUpId,
                file,
                file.name
              );
            } catch (attachmentError) {
              console.error('Error uploading attachment:', attachmentError);
              showError(`Error uploading ${file.name}`);
            }
          }
        }
        
        showSuccess('Follow-up added successfully');
        setShowCreateFollowUp(false);
        setFollowUpForm({
          progression_percentage: 0,
          recommendation_status_selected: 'in_progress',
          notes: '',
          attachments: []
        });
        fetchFollowUps(); // Refresh follow-ups
        fetchRecommendationDetails(); // Refresh recommendation (status might have changed)
      } else {
        showError(result.error || 'Error creating follow-up');
      }
    } catch (error) {
      console.error('Error creating follow-up:', error);
      showError('Error creating follow-up');
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleEditFollowUp = (followUp) => {
    setEditingFollowUp(followUp.id);
    setEditFollowUpForm({
      progression_percentage: followUp.progression_percentage,
      recommendation_status_selected: followUp.recommendation_status_selected,
      notes: followUp.notes,
      attachments: [] // New attachments to add
    });
  };

  const handleUpdateFollowUp = async (e) => {
    e.preventDefault();
    
    if (!editFollowUpForm.notes.trim()) {
      showError('Notes are required');
      return;
    }

    setFollowUpLoading(true);
    
    try {
      // Update the follow-up
      const followUpData = myRecommendationService.formatFollowUpData(editFollowUpForm);
      const result = await myRecommendationService.updateFollowUp(
        authTokens.access, 
        id, 
        editingFollowUp,
        followUpData
      );
      
      if (result.success) {
        // Upload new attachments if any
        if (editFollowUpForm.attachments && editFollowUpForm.attachments.length > 0) {
          for (const file of editFollowUpForm.attachments) {
            try {
              await myRecommendationService.uploadFollowUpAttachment(
                authTokens.access,
                id,
                editingFollowUp,
                file,
                file.name
              );
            } catch (attachmentError) {
              console.error('Error uploading attachment:', attachmentError);
              showError(`Error uploading ${file.name}`);
            }
          }
        }
        
        showSuccess('Follow-up updated successfully');
        setEditingFollowUp(null);
        setEditFollowUpForm({
          progression_percentage: 0,
          recommendation_status_selected: 'in_progress',
          notes: '',
          attachments: []
        });
        fetchFollowUps(); // Refresh follow-ups
        fetchRecommendationDetails(); // Refresh recommendation (status might have changed)
      } else {
        showError(result.error || 'Error updating follow-up');
      }
    } catch (error) {
      console.error('Error updating follow-up:', error);
      showError('Error updating follow-up');
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingFollowUp(null);
    setEditFollowUpForm({
      progression_percentage: 0,
      recommendation_status_selected: 'in_progress',
      notes: '',
      attachments: []
    });
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const result = await followupAttachmentService.downloadAttachment(
        authTokens.access,
        attachment.id,
        attachment.file_name
      );
      
      if (!result.success) {
        showError(result.error || 'Error downloading attachment');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError('Error downloading attachment');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente', icon: Clock },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours', icon: Play },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: CheckCircle },
      achieved: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Réalisé', icon: Award },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'En retard', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la recommandation...</p>
        </div>
      </div>
    );
  }

  if (!recommendation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Recommandation non trouvée</p>
        <Link to="/dashboard/audit/my-recommendations" className="text-green-600 hover:underline mt-4 inline-block">
          Retour à mes recommandations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/audit/my-recommendations"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Détails de ma recommandation</h1>
            <p className="text-sm text-gray-600">
              Créé le {new Date(recommendation.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(recommendation.status)}
          {canManage && (
            <div className="flex items-center space-x-2">
              {/* Start button (only if pending) */}
              {recommendation.status === 'pending' && (
                <button
                  onClick={handleStartRecommendation}
                  disabled={actionLoading.start}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading.start ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {actionLoading.start ? 'Démarrage...' : 'Démarrer'}
                </button>
              )}

              {/* Complete button (only if in progress) */}
              {recommendation.status === 'in_progress' && (
                <button
                  onClick={handleCompleteRecommendation}
                  disabled={actionLoading.complete}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading.complete ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {actionLoading.complete ? 'Clôture...' : 'Clôturer'}
                </button>
              )}

              {/* Achieve button (only if completed) */}
              {recommendation.status === 'completed' && (
                <button
                  onClick={handleAchieveRecommendation}
                  disabled={actionLoading.achieve}
                  className="inline-flex items-center px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading.achieve ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                  ) : (
                    <Award className="h-4 w-4 mr-2" />
                  )}
                  {actionLoading.achieve ? 'Réalisation...' : 'Archiver'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Question Context */}
      {recommendation.question && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-900">Question associée</h3>
              <Link
                to={`/dashboard/audit/questions/${recommendation.question.id}`}
                className="text-sm text-green-700 hover:text-green-900 font-medium"
              >
                {recommendation.question.question_name}
              </Link>
              <div className="flex items-center space-x-4 mt-2 text-xs text-green-600">
                <span>Section: {recommendation.question.audit_section?.section_name}</span>
                <span>Statut: {recommendation.question.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations sur la recommandation</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Delay */}
            <div>
              <div className="flex items-center mb-2">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-700">Date d'achèvement cible</p>
              </div>
              <p className="text-sm text-gray-900 ml-6">
                {recommendation.delay_of_recommendation 
                  ? new Date(recommendation.delay_of_recommendation).toLocaleDateString()
                  : 'Non défini'
                }
              </p>
            </div>

            {/* Created By */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Créé par</p>
              <p className="text-sm text-gray-900">{recommendation.created_by_name || 'Inconnu'}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Dates */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Dates importantes</p>
              <div className="space-y-1 text-sm text-gray-900">
                <p>Créé: {new Date(recommendation.created_at).toLocaleDateString()}</p>
                <p>Mis à jour: {new Date(recommendation.updated_at).toLocaleDateString()}</p>
                {recommendation.started_at && (
                  <p>Commencé: {new Date(recommendation.started_at).toLocaleDateString()}</p>
                )}
                {recommendation.completed_at && (
                  <p>Terminé: {new Date(recommendation.completed_at).toLocaleDateString()}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Recommendation */}
      {(recommendation.recommendation_text || recommendation.question_recommendation) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommandation originale de la question</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-900">
              {recommendation.recommendation_text || 
               (typeof recommendation.question_recommendation === 'object' 
                 ? recommendation.question_recommendation?.recommendation 
                 : recommendation.question_recommendation)}
            </p>
            {recommendation.question_recommendation?.priority && (
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  recommendation.question_recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                  recommendation.question_recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  Priorité: {recommendation.question_recommendation.priority}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Elements of Recommendation */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Target className="h-5 w-5 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Éléments de la recommandation</h3>
        </div>
        <div className="prose max-w-none">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{recommendation.elements_of_recommendation}</p>
        </div>
      </div>

      {/* KPI of Recommendation */}
      {recommendation.kpi_of_recommendation && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Indicateurs de performance clés</h3>
          <div className="prose max-w-none">
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{recommendation.kpi_of_recommendation}</p>
          </div>
        </div>
      )}

      {/* Follow-ups Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Suivis et mises à jour de progression</h3>
          {canManage && (recommendation.status === 'in_progress' || recommendation.status === 'pending') && (
            <button
              onClick={() => setShowCreateFollowUp(true)}
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un suivi
            </button>
          )}
        </div>

        {/* Create Follow-up Form */}
        {showCreateFollowUp && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Ajouter une mise à jour de progression</h4>
            <form onSubmit={handleCreateFollowUp} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Progress Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Progress Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={followUpForm.progression_percentage}
                      onChange={(e) => setFollowUpForm(prev => ({ 
                        ...prev, 
                        progression_percentage: parseInt(e.target.value) 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0-100"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">%</span>
                  </div>
                  {/* Progress Bar Preview */}
                  {followUpForm.progression_percentage  <= 100 ? (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${followUpForm.progression_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ):(
                    <p>Le pourcentage ne doit pas dépasser 100%.</p>
                  )}
                </div>

                {/* Recommendation Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mettre à jour l'état de la recommandation *
                  </label>
                  <select
                    value={followUpForm.recommendation_status_selected}
                    onChange={(e) => setFollowUpForm(prev => ({ 
                      ...prev, 
                      recommendation_status_selected: e.target.value 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminée</option>
                    <option value="achieved">Archiver</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes *
                </label>
                <textarea
                  value={followUpForm.notes}
                  onChange={(e) => setFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Décrivez la progression, les défis, les prochaines étapes ou toute mise à jour pertinente..."
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pièces jointes (Optionnel - Plusieurs fichiers autorisés)
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFollowUpForm(prev => ({ 
                    ...prev, 
                    attachments: Array.from(e.target.files || [])
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vous pouvez sélectionner plusieurs fichiers. Formats supportés: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF
                </p>
                {followUpForm.attachments && followUpForm.attachments.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">Fichiers sélectionnés:</p>
                    <ul className="space-y-1">
                      {followUpForm.attachments.map((file, index) => (
                        <li key={index} className="flex items-center justify-between text-sm text-gray-700 bg-gray-50 px-2 py-1 rounded">
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newAttachments = followUpForm.attachments.filter((_, i) => i !== index);
                              setFollowUpForm(prev => ({ ...prev, attachments: newAttachments }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateFollowUp(false);
                    setFollowUpForm({
                      progression_percentage: 0,
                      recommendation_status_selected: 'in_progress',
                      notes: '',
                      attachments: []
                    });
                  }}
                  className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={followUpLoading}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {followUpLoading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                      Ajout...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter un suivi
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {followUpsLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : followUps.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Aucun suivi pour le moment</p>
            {canManage && (recommendation.status === 'in_progress' || recommendation.status === 'pending') && (
              <button
                onClick={() => setShowCreateFollowUp(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer le premier suivi
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {followUps.map((followUp) => {
              const isExpanded = expandedFollowUps.has(followUp.id);
              
              return (
                <div key={followUp.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {followUp.progression_percentage}% Complete
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${followUp.progression_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        followUp.recommendation_status_selected === 'achieved' ? 'bg-yellow-100 text-yellow-800' :
                        followUp.recommendation_status_selected === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        Statut: {followUp.recommendation_status_selected.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {new Date(followUp.created_at).toLocaleDateString()}
                      </span>
                      {canManage && (recommendation.status === 'in_progress' || recommendation.status === 'pending') && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedFollowUps);
                              if (isExpanded) {
                                newExpanded.delete(followUp.id);
                              } else {
                                newExpanded.add(followUp.id);
                              }
                              setExpandedFollowUps(newExpanded);
                            }}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                            title={isExpanded ? "Réduire les détails" : "Développer les détails"}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditFollowUp(followUp)}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                            title="Modifier le suivi"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Edit Form */}
                  {editingFollowUp === followUp.id ? (
                    <form onSubmit={handleUpdateFollowUp} className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50">
                      <h4 className="text-sm font-medium text-gray-900 mb-4">Modifier le suivi</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Progress Percentage */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pourcentage de progression *
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={editFollowUpForm.progression_percentage}
                              onChange={(e) => setEditFollowUpForm(prev => ({ 
                                ...prev, 
                                progression_percentage: parseInt(e.target.value) || 0 
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="0-100"
                            />
                            <span className="absolute right-3 top-2 text-gray-500">%</span>
                          </div>
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${editFollowUpForm.progression_percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        {/* Recommendation Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mettre à jour le statut de la recommandation *
                          </label>
                          <select
                            value={editFollowUpForm.recommendation_status_selected}
                            onChange={(e) => setEditFollowUpForm(prev => ({ 
                              ...prev, 
                              recommendation_status_selected: e.target.value 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="in_progress">Garder en cours</option>
                            <option value="completed">Marquer comme terminé</option>
                            <option value="achieved">Marquer comme réalisé</option>
                          </select>
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes *
                        </label>
                        <textarea
                          value={editFollowUpForm.notes}
                          onChange={(e) => setEditFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Décrivez la progression, les défis, les prochaines étapes ou toute mise à jour pertinente..."
                        />
                      </div>

                      {/* Additional Attachments */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ajouter de nouvelles pièces jointes (Optionnel - Plusieurs fichiers autorisés)
                        </label>
                        <input
                          type="file"
                          multiple
                          onChange={(e) => setEditFollowUpForm(prev => ({ 
                            ...prev, 
                            attachments: Array.from(e.target.files || [])
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Sélectionnez des fichiers supplémentaires à ajouter à ce suivi. Les pièces jointes existantes resteront inchangées.
                        </p>
                        {editFollowUpForm.attachments && editFollowUpForm.attachments.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600 mb-2">Nouveaux fichiers à ajouter:</p>
                            <ul className="space-y-1">
                              {editFollowUpForm.attachments.map((file, index) => (
                                <li key={index} className="flex items-center justify-between text-sm text-gray-700 bg-gray-50 px-2 py-1 rounded">
                                  <span>{file.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAttachments = editFollowUpForm.attachments.filter((_, i) => i !== index);
                                      setEditFollowUpForm(prev => ({ ...prev, attachments: newAttachments }));
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Supprimer
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Form Actions */}
                      <div className="flex items-center justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={followUpLoading}
                          className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          {followUpLoading ? (
                            <>
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                              Updating...
                            </>
                          ) : (
                            <>
                              <Edit className="h-4 w-4 mr-2" />
                              Update Follow-up
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="text-sm text-gray-900 mb-2">{followUp.notes}</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      By {followUp.created_by_name} • {followUp.attachment_count || 0} attachment(s)
                    </p>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Attachments</h5>
                      {followUp.attachments && followUp.attachments.length > 0 ? (
                        <div className="space-y-2">
                          {followUp.attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <FileText className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{attachment.file_name}</span>
                                <span className="text-xs text-gray-500">
                                  ({Math.round(attachment.file_size / 1024)} KB)
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No attachments</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>


    </div>
  );
}